import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
    TextField, Button, Typography, Avatar, InputAdornment, IconButton,
    Chip, Dialog, DialogTitle, DialogContent, DialogActions, Box,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse } from '@mui/material';
import { stringAvatar } from '../../utils';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useActivityReport } from './hooks';
import { getById as getCourseById, getLessonPlan, getLevelCourseMapping } from '../../store/course/courseBaseApi';
import _, { set } from 'lodash';
import Loader from '../../components/Loader';
import { getById } from '../../store/student/studentBaseApi';
import {
    scoringActions,
    getStudentTests,
    isStudentTestsLoading,
    getAttemptHistory,
    isAttemptHistoryLoading,
    getAssessmentSummary,
    isAssessmentSummaryLoading,
    getStrengthsWeaknesses,
    isStrengthsWeaknessesLoading,
} from '../../store/scoring/scoringSlice';
import { getActivities } from '../../store/study/studyBaseApi';
import { aggregateChapterScores } from './chapterScoreAggregator';

const useStyles = makeStyles()((theme) => ({
    dashboardContainer: {
        display: 'flex',
        height: 'calc(100vh - 118px)',
    },
    studentSidebar: {
        width: '350px',
        backgroundColor: '#ffffff',
        borderRight: '1px solid #e0e4e8',
        padding: '25px',
        overflowY: 'auto',
    },
    courseList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
    },
    levelGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    levelHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 2px',
        color: '#002147',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.6px',
        textTransform: 'uppercase',
        borderBottom: '1px solid #E5E7EB',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'color .15s ease',
        '&:hover': {
            color: '#0EA5E9',
        },
    },
    levelHeaderTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        minWidth: 0,
    },
    levelChevron: {
        fontSize: '1.1rem',
        transition: 'transform .2s ease',
        '&.collapsed': {
            transform: 'rotate(-90deg)',
        },
    },
    levelCount: {
        color: '#94A3B8',
        fontSize: '0.68rem',
        fontWeight: 600,
        letterSpacing: '0.2px',
        textTransform: 'none',
    },
    levelCourses: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        paddingTop: '10px',
    },
    studentItem: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 18px',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'background-color .2s ease, border-color .2s ease, transform .15s ease',
        border: '1px solid #E5E7EB',
        backgroundColor: '#ffffff',
        '&:hover': {
            backgroundColor: '#F8FAFC',
            borderColor: '#CBD5E1',
            transform: 'translateY(-1px)',
        },
        '&.active': {
            backgroundColor: '#F1F5F9',
            color: '#0F172A',
            borderColor: '#002147',
            borderLeft: '4px solid #002147',
            paddingLeft: '15px',
        },
    },
    gradeNameText: {
        marginTop: theme.spacing(0.5),
        color: '#64748B',
        fontSize: '0.78rem',
        letterSpacing: '0.2px',
    },
    reportArea: {
        flexGrow: 1,
        backgroundColor: '#f4f6f8',
        padding: '40px',
        overflowY: 'auto',
    },
    reportHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e0e4e8',
    },
    studentName: {
        fontSize: '2rem',
        fontWeight: 'bold',
        marginBottom: theme.spacing(1),
    },
    avatar: {
        backgroundColor: 'var(--mui-palette-primary-main)',
        color: '#fff',
        height: '80px',
        width: '80px',
    },
    detailedStats: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        margin: '30px 0',
    },
    specificsSection: {
        fontSize: '1.5rem',
        marginBottom: theme.spacing(3),
        fontWeight: 600
    },
    statCard: {
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        padding: '20px',
        textAlign: 'center',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 6px 15px rgba(0, 0, 0, 0.08)',
        },
    },
    statNumber: {
        fontSize: '1.8rem',
        fontWeight: 'bold',
        color: 'var(--mui-palette-primary-main)',
        marginBottom: '10px',
    },
    exceptionGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '25px',
    },
    exceptionCard: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '25px',
        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.08)',
        borderLeft: '6px solid',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)',
        },
    },
    exceptionTitle: {
        fontSize: '1.1rem',
        marginBottom: theme.spacing(2)
    },
    exceptionBody: {
        marginBottom: theme.spacing(2),
        flexGrow: 1,
        fontSize: '.85rem'
    },
    exceptionCardFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingTop: '10px',
        borderTop: '1px solid #f1f1f1',
    },
    exceptionTag: {
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        marginLeft: '10px',
        textTransform: 'capitalize',
    },
    searchInput: {
        position: 'relative',
        marginBottom: '25px',
    },
    strength: {
        backgroundColor: '#e6f7ff',
        color: '#1890ff',
    },
    weakness: {
        backgroundColor: '#fff1f0',
        color: '#f5222d',
    }
}));

/** Colors for the tier chip on each strength/weakness row. */
const TIER_STYLES = {
    good: { bg: alpha('#16A34A', 0.12), fg: '#16A34A', label: 'Strong' },
    ok:   { bg: alpha('#D97706', 0.12), fg: '#D97706', label: 'Mid' },
    low:  { bg: alpha('#DC2626', 0.12), fg: '#DC2626', label: 'Low' },
};

/**
 * Renders the aggregated Strengths / Needs Attention cards for one (student, course).
 * Each card is a "named area" (chapter for reading, assessment for tests) so the
 * teacher sees WHERE the student is strong/weak, not just a list of raw events.
 *
 * Lists are ranked relative to the student (top-3 strongest / bottom-3 weakest),
 * so even an average student gets a populated report. Tier chips expose the
 * absolute band (good / mid / low) so the teacher reads the row honestly.
 */
const StrengthsWeaknessesSection = ({ data, loading }) => {
    const strengths = Array.isArray(data?.strengths) ? data.strengths : [];
    const weaknesses = Array.isArray(data?.weaknesses) ? data.weaknesses : [];
    const total = data?.scoredAreaCount || 0;

    if (loading) {
        return <Loader sx={{ backgroundColor: 'transparent' }} />;
    }

    const Card = ({ title, items, kind }) => {
        const isStrength = kind === 'strength';
        const accent = isStrength ? '#16A34A' : '#DC2626';
        const titleBg = alpha(accent, 0.08);
        return (
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    background: titleBg,
                    color: accent,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}>
                    <span>{title}</span>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.8 }}>
                        {items.length} of {total}
                    </span>
                </Box>
                {items.length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#6B7280', px: 1 }}>
                        {total === 0
                            ? 'No scored areas yet for this course.'
                            : isStrength ? 'Not enough data yet.' : 'Nothing to flag yet.'}
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {items.map((it) => {
                            const tier = TIER_STYLES[it.tier] || TIER_STYLES.ok;
                            const scoreText = it.score == null ? '—' : `${Math.round(it.score)}`;
                            return (
                                <Box
                                    key={`${it.kind}-${it.id}`}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        background: '#fff',
                                        border: `1px solid ${alpha(accent, 0.18)}`,
                                        borderLeft: `4px solid ${accent}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography
                                            sx={{ fontWeight: 600, fontSize: '0.92rem' }}
                                            noWrap
                                        >
                                            {it.name || (it.id ? String(it.id) : 'Skill')}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#6B7280', display: 'block', mt: 0.25 }}
                                        >
                                            {it.detail || ''}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <Chip
                                            label={scoreText}
                                            size="small"
                                            sx={{
                                                background: tier.bg,
                                                color: tier.fg,
                                                fontWeight: 700,
                                                minWidth: 44,
                                            }}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <>
            <Typography variant="h6" className="" gutterBottom sx={{ fontWeight: 700, mt: 3, mb: 2 }}>
                Strengths &amp; Needs Attention
                <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 1.5, color: '#6B7280', fontWeight: 500 }}
                >
                    {total === 0
                        ? 'No measurable skill data yet for this course'
                        : `Skill areas with measurable data (${total} skill${total === 1 ? '' : 's'})`}
                </Typography>
            </Typography>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
                mb: 4,
            }}>
                <Card title="Strengths" items={strengths} kind="strength" />
                <Card title="Needs attention" items={weaknesses} kind="weakness" />
            </Box>
        </>
    );
};

export const Reports = () => {
    const { classes } = useStyles();
    const { studentId } = useParams();
    const dispatch = useDispatch();
    const { reports, loadReports, courses, setCourses } = useActivityReport();
    const [exceptionReport, setExceptionReport] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState({});
    const [userData, setUserData] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [lessonPlan, setLessonPlan] = useState({});
    const [filteredData, setFilteredData] = useState([]);
    const [totalIndicators, setTotalIndicators] = useState(0);
    const [weaknesses, setWeaknesses] = useState([]);
    const [attemptModalAssessmentId, setAttemptModalAssessmentId] = useState(null);
    const [attemptModalChapterId, setAttemptModalChapterId] = useState(null);
    // IR level -> course mapping, used to group the sidebar courses by level.
    const [levelMapping, setLevelMapping] = useState([]);
    // Set of collapsed level group keys. Empty = all expanded (the default).
    const [collapsedLevels, setCollapsedLevels] = useState(() => new Set());

    const toggleLevel = (key) => {
        setCollapsedLevels((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const testScores = useSelector(getStudentTests);
    const testScoresLoading = useSelector(isStudentTestsLoading);
    const attemptHistory = useSelector(getAttemptHistory);
    const attemptHistoryLoading = useSelector(isAttemptHistoryLoading);
    const assessmentSummary = useSelector(getAssessmentSummary);
    const assessmentSummaryLoading = useSelector(isAssessmentSummaryLoading);
    const strengthsWeaknesses = useSelector(getStrengthsWeaknesses);
    const strengthsWeaknessesLoading = useSelector(isStrengthsWeaknessesLoading);

    // Live chapter scores — computed client-side from the master-service activity log
    // (/v1/api/master/assignmentProgress/activity/{courseId}/{studentId}). This is the
    // source-of-truth the production study UI already reads, so we don't have to wait
    // for our local ir_chapter_attempt table to be populated.
    const [chapterScores, setChapterScores] = useState([]);
    const [chapterScoresLoading, setChapterScoresLoading] = useState(false);
    // Raw activity log is retained as the input to the session aggregator.
    const [rawActivities, setRawActivities] = useState([]);
    const [localAttemptHistory, setLocalAttemptHistory] = useState(null); // {kind:'chapter', ...} for the modal

    const typeMappings = {
        SPEAK_ALOUD: {
            title: 'Pronounciation of words',
            remarks: 'Pronounciation of words look proper and clear',
        },
        INCORRECT_SPEAK_ALOUD: {
            title: 'Incorrect Pronounciation',
            remarks: 'Hesitation and pauses during oral presentations',
        },
        CHAPTER_COMPLETED: {
            title: 'Chapter Completed',
            remarks: 'Chapter has been completed successfully',
        },
        ASSESSMENT: {
            title: 'Assessment',
            remarks: 'Assessment has been completed successfully',
        },
    }

    const handleSelectionClick = (course) => {
        setSelectedCourse(course);
        setTotalIndicators(course.indicators);
        setWeaknesses(course.weaknesses);
        setExceptionReport(_.shuffle([...course.strengthsMap, ...course.weaknessesMap]));
    };

    // Header shows the student's real name. userName is the login handle and is only
    // a fallback for records with no first/last name — the DTO sends those as null
    // rather than empty strings, so a plain concat + trim is enough to detect it.
    const studentName = useMemo(() => {
        const full = `${userData?.firstName ?? ''} ${userData?.lastName ?? ''}`.trim();
        return full || userData?.userName || '';
    }, [userData?.firstName, userData?.lastName, userData?.userName]);

    const avatar = useMemo(() => stringAvatar(studentName), [studentName]);

    // courseId -> { levelName, levelValue, order } built from the IR level mapping.
    // Ids are trimmed on both sides because courses.id is a space-padded CHAR(32).
    const levelByCourseId = useMemo(() => {
        const map = {};
        (Array.isArray(levelMapping) ? levelMapping : []).forEach((lvl) => {
            (lvl?.subLevels || []).forEach((sub) => {
                if (!sub?.irCourseId) return;
                map[String(sub.irCourseId).trim()] = {
                    levelName: lvl.irLevel || 'Level',
                    levelValue: Number.isFinite(lvl.value) ? lvl.value : Number.MAX_SAFE_INTEGER,
                    order: sub.order ?? 0,
                };
            });
        });
        return map;
    }, [levelMapping]);

    // Sidebar courses grouped under their IR level. Courses with no mapping fall into
    // a trailing "Other courses" group so nothing is ever hidden.
    const courseGroups = useMemo(() => {
        const UNMAPPED_KEY = '__unmapped__';
        const groups = new Map();
        (Array.isArray(filteredData) ? filteredData : []).forEach((course) => {
            const info = levelByCourseId[String(course?.id || '').trim()];
            const key = info ? String(info.levelValue) + '::' + info.levelName : UNMAPPED_KEY;
            if (!groups.has(key)) {
                groups.set(key, {
                    key,
                    levelName: info ? info.levelName : 'Other courses',
                    levelValue: info ? info.levelValue : Number.MAX_SAFE_INTEGER,
                    courses: [],
                });
            }
            groups.get(key).courses.push({ ...course, _order: info?.order ?? 0 });
        });
        const arr = Array.from(groups.values());
        // Sort courses within a level by their configured order, then by name.
        arr.forEach((g) =>
            g.courses.sort((a, b) =>
                (a._order - b._order) ||
                String(a?.courseName || '').localeCompare(String(b?.courseName || ''), undefined, {
                    numeric: true,
                    sensitivity: 'base',
                })
            )
        );
        // Sort levels by their numeric value; the unmapped group sinks to the bottom.
        arr.sort((a, b) => a.levelValue - b.levelValue);
        return arr;
    }, [filteredData, levelByCourseId]);

    const ExceptionType = ({ specifics }) => {
        let classname = '';
        if (specifics.score > 0) {
            classname = 'strength';
        } else {
            classname = 'weakness';
        }

        return <div className={classes[classname] + ' ' + classes.exceptionTag}>{classname}</div>
    }

    const handleApplyFilters = ({ chapterId, sectionId }) => {
        if (!sectionId || !lessonPlan?.sections?.length) return;

        const prepareAndSetReportMap = (report, field, id) => {
            let strengthsFound = {}, weaknessesFound = {};

            let strengths = report.strengths.filter(strength => strength[field] == id);
            let weaknesses = report.weaknesses.filter(weakness => weakness[field] == id);

            strengthsFound.strengthsMap = strengths.map(strength => {
                if (!strengthsFound[strength.activityType]) {
                    strengthsFound[strength.activityType] = true;
                    return strength;
                }
            }).filter(strength => strength);
            strengthsFound.indicators = strengthsFound.strengthsMap.length;
            strengthsFound.strengths = strengths;

            weaknessesFound.weaknessesMap = weaknesses.map(weakness => {
                if (!weaknessesFound[weakness.activityType]) {
                    weaknessesFound[weakness.activityType] = true;
                    return weakness;
                }
            }).filter(weakness => weakness);
            weaknessesFound.indicators = weaknessesFound.weaknessesMap.length;
            weaknessesFound.weaknesses = weaknesses;

            setExceptionReport(_.shuffle([...strengthsFound.strengthsMap, ...weaknessesFound.weaknessesMap]));
            setTotalIndicators(strengthsFound.indicators + weaknessesFound.indicators);
            setWeaknesses(weaknessesFound.weaknesses);
          
        };

        if (chapterId) {
            prepareAndSetReportMap(selectedCourse, 'chapterId', chapterId);
        } else if (sectionId) {
            prepareAndSetReportMap(selectedCourse, 'sectionId', sectionId);
        }
    };

    const handleClearFilters = () => {
        handleSelectionClick(selectedCourse);
        setTotalIndicators(selectedCourse.indicators);
        setWeaknesses(selectedCourse.weaknesses);
        setDialogOpen(false);
    }

    const handleSearch = (e) => {
        let data = courses.filter((course) => course.courseName.toLowerCase().includes(String(e.target.value).toLowerCase()));
        setFilteredData(data);
        setSearchTerm(e.target.value);
    }

    const clearSearch = () => {
        setFilteredData(courses);
        searchTerm('');
    }

    useEffect(() => {
        const requiredCourseFields = ['id', 'courseName', 'description', 'code'];
        const loadAllCoursesFromReports = async () => {
            const reportsWithData = await Promise.all(reports.map(async (report) => {
                // Defensive: a single corrupt course (e.g. master-service returning 500
                // because of a bad enum or JSONB column for that row) must not take down
                // the whole reports page. Fall back to placeholder metadata if the
                // course fetch fails so the row still renders.
                let data = null;
                try {
                    const resp = await getCourseById({ id: report.courseId });
                    data = resp?.data ?? null;
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `Failed to load course ${report.courseId}; using placeholder metadata.`,
                        err
                    );
                }
                let obj = {}, strengthsFound = {}, weaknessesFound = {};

                if (data && Object.keys(data).length) {
                    requiredCourseFields.forEach(field => {
                        obj[field] = data[field];
                    });
                } else {
                    // Placeholder metadata so the row still renders in the sidebar.
                    obj.id = report.courseId;
                    obj.courseName = '(unable to load course)';
                    obj.code = '—';
                    obj.description = '';
                }
                if (report.strengths) {
                    obj.strengthsMap = report.strengths.map(strength => {
                        if (!strengthsFound[strength.activityType]) {
                            strengthsFound[strength.activityType] = true;
                            return strength;
                        }
                    }).filter(strength => strength);
                    obj.indicators = obj.strengthsMap.length;
                    obj.strengths = report.strengths;
                }
                if (report.weaknesses) {
                    obj.weaknessesMap = report.weaknesses.map(weakness => {
                        if (!weaknessesFound[weakness.activityType]) {
                            weaknessesFound[weakness.activityType] = true;
                            return weakness;
                        }
                    }).filter(weakness => weakness);
                    obj.indicators += obj.weaknessesMap.length;
                    obj.weaknesses = report.weaknesses;
                }
                obj.score = report.totalScore || 0;

                return obj;
            }));

            setCourses(reportsWithData);
            setFilteredData(reportsWithData);
            handleSelectionClick(reportsWithData[0]);
            setIsLoading(false);
        }

        if (reports && reports.length) {
            loadAllCoursesFromReports();
        } else if (reports) {
            // `reports` resolved but is empty — nothing to load, stop the loader so the
            // sidebar shows the empty-state instead of spinning forever.
            setCourses([]);
            setFilteredData([]);
            setIsLoading(false);
        }
    }, [reports]);

    useEffect(() => {
        if (selectedCourse && Object.keys(selectedCourse).length) {
            getLessonPlan(selectedCourse).then(({ data }) => {
                setLessonPlan(data.lessonPlan);
            });
        }
    }, [selectedCourse]);

    useEffect(() => {
        // The sidebar lists every course this student has activity in and has no
        // pagination UI, so we must fetch them ALL. The endpoint defaults to a page
        // size of 5 (see UserPerformanceReportController), which silently dropped
        // courses for students with more than 5 — pass a high limit to get the full set.
        loadReports({ userId: studentId, query: { fields: 'courseId,strengths,weaknesses,totalScore', limit: 1000 } });
        getById({ id: studentId }).then(({ data }) => {
            setUserData(data);
        });
        // Level -> course mapping, so the sidebar can group courses by IR level.
        // Failure is non-fatal — the sidebar just falls back to a single ungrouped list.
        getLevelCourseMapping()
            .then((resp) => setLevelMapping(Array.isArray(resp?.data) ? resp.data : []))
            .catch(() => setLevelMapping([]));
        // Assessment summary is course-scoped — it loads when the user picks a course
        // (see the effect on selectedCourse below).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When the user picks a course, fetch the master-service activity log for
    // (course, student) and aggregate it into per-chapter Reading Sessions.
    useEffect(() => {
        const courseId = selectedCourse?.id;
        if (!courseId || !studentId) {
            setChapterScores([]);
            setRawActivities([]);
            return;
        }
        // Per-(student, course) assessment summary — sourced from ir_assignment_progress.
        // Replaces the legacy per-attempt Test Scores section in this report.
        dispatch(scoringActions.loadAssessmentSummary({ studentId, courseId }));
        // Aggregated Strengths / Needs Attention areas — dispatch is paused while
        // the section is hidden. Re-enable alongside the JSX block in the report below.
        // dispatch(scoringActions.loadStrengthsWeaknesses({ studentId, courseId }));
        let cancelled = false;
        setChapterScoresLoading(true);
        getActivities({ params: { courseId, assigneeId: studentId } })
            .then((resp) => {
                if (cancelled) return;
                const activities = resp?.data?.activities || [];
                setRawActivities(activities);
                setChapterScores(aggregateChapterScores(activities));
            })
            .catch(() => {
                if (cancelled) return;
                setChapterScores([]);
                setRawActivities([]);
            })
            .finally(() => {
                if (!cancelled) setChapterScoresLoading(false);
            });
        return () => { cancelled = true; };
    }, [selectedCourse?.id, studentId]);

    const openAttemptHistory = (group) => {
        // Build the modal payload locally from the grouped assessment-summary rows
        // (the canonical child-table source), mirroring the chapter path below. The
        // legacy /tests/{id}/attempts endpoint is keyed by chapterId and empty here.
        setAttemptModalAssessmentId(group.assessmentId);
        setAttemptModalChapterId(null);
        setLocalAttemptHistory({
            kind: 'assessment',
            studentId,
            assessmentId: group.assessmentId,
            assessmentTitle: group.assessmentTitle,
            attemptCount: group.attemptCount,
            bestPercentage: group.bestPct,
            attempts: group.attempts || [],
        });
    };
    const openChapterAttemptHistory = (chapterId) => {
        setAttemptModalChapterId(chapterId);
        setAttemptModalAssessmentId(null);
        // Build the modal payload directly from the aggregated data we already have.
        const chapter = (chapterScores || []).find((c) => c.chapterId === chapterId);
        if (chapter) {
            setLocalAttemptHistory({
                kind: 'chapter',
                studentId,
                chapterId: chapter.chapterId,
                chapterName: chapter.chapterName,
                sectionName: chapter.sectionName,
                attemptCount: chapter.attemptCount,
                bestAverage: chapter.bestAverage,
                latestAverage: chapter.latestAverage,
                attempts: chapter.attempts || [],
            });
        } else {
            setLocalAttemptHistory(null);
        }
    };
    const closeAttemptHistory = () => {
        setAttemptModalAssessmentId(null);
        setAttemptModalChapterId(null);
        setLocalAttemptHistory(null);
        setExpandedAttempts(new Set());
        dispatch(scoringActions.clearAttemptHistory());
    };
    // Which attempt rows have their per-sentence list expanded (keyed by attemptNumber).
    const [expandedAttempts, setExpandedAttempts] = useState(() => new Set());
    // Accordion behaviour: only one attempt's sentence list open at a time.
    const toggleAttempt = (key) =>
        setExpandedAttempts((prev) => (prev.has(key) ? new Set() : new Set([key])));
    const attemptModalOpen = !!(attemptModalAssessmentId || attemptModalChapterId);
    // Modal data source: both chapter and assessment attempts are aggregated locally; the
    // redux `attemptHistory` is a legacy fallback with no remaining producer.
    const activeAttemptHistory = localAttemptHistory || attemptHistory;
    const activeAttemptHistoryLoading = localAttemptHistory ? false : attemptHistoryLoading;

    const colorForPct = (pct) => {
        if (pct == null) return { bg: alpha('#6B7280', 0.12), fg: '#6B7280' };
        if (pct >= 80) return { bg: alpha('#16A34A', 0.12), fg: '#16A34A' };
        if (pct >= 60) return { bg: alpha('#D97706', 0.12), fg: '#D97706' };
        return { bg: alpha('#DC2626', 0.12), fg: '#DC2626' };
    };

    const formatPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
    // Reading sessions: 0..100 accuracy values, shown as a rounded percentage.
    const formatScore = (v) => (v == null ? '—' : `${Math.round(Number(v))}%`);

    // An MCQ assessment summary row as a 0..100 percentage (scored/total*100, clamped), or null
    // when it carries no gradeable score. Shared by the chapter and course average blends.
    const assessmentPct = (a) => {
        const scored = Number(a && a.scored);
        const total = Number(a && a.total);
        if (!Number.isFinite(scored) || !Number.isFinite(total) || total <= 0) return null;
        return Math.min(100, (Math.max(0, scored) / total) * 100);
    };

    // Use the same sentence set as the session cards. aggregateChapterScores has
    // already removed echoed payloads from the activity log, so reading rollups
    // must be derived from these attempt sentences rather than raw events.
    const readingAverageForSessions = (sessions) => {
        let sum = 0;
        let count = 0;
        for (const session of sessions || []) {
            for (const attempt of session.attempts || []) {
                for (const sentence of attempt.sentences || []) {
                    const score = Number(sentence && sentence.score);
                    if (Number.isFinite(score) && score > 0) {
                        sum += score;
                        count += 1;
                    }
                }
            }
        }
        return count ? sum / count : null;
    };

    // The activity log carries no activity type, so a synonyms / vocabulary chapter that also
    // has the student read a word aloud emits a SPEAK_ALOUD and surfaces as a phantom
    // 1-sentence "session". Use the lesson plan (chapterId -> chapterType) to keep only
    // genuine reading chapters; everything else (synonyms, assessment, recap, summaries) is
    // dropped before grouping.
    const readingChapterScores = useMemo(() => {
        // Genuine reading chapter types. Tune this list to change what counts as a session
        // (e.g. drop 'story' to show only the sentence-reads).
        const READING_CHAPTER_TYPES = new Set(['story-with-keywords', 'story']);
        const sessions = Array.isArray(chapterScores) ? chapterScores : [];
        const norm = (t) => String(t || '').trim().replace(/\s+/g, '-').toLowerCase();
        const typeById = {};
        (lessonPlan?.sections || []).forEach((sec) => {
            (sec?.chapters || []).forEach((ch) => {
                if (ch?.chapterId) typeById[ch.chapterId] = norm(ch.chapterType);
            });
        });
        if (Object.keys(typeById).length === 0) return sessions; // lesson plan not loaded yet
        const filtered = sessions.filter((s) => READING_CHAPTER_TYPES.has(typeById[s.chapterId]));
        return filtered.length > 0 ? filtered : sessions; // safety net vs blanking the report
    }, [chapterScores, lessonPlan]);

    // Group sessions (API-named "chapters") under their parent chapter (API-named "section").
    // Compute a chapter-level rollup: avg of session-best scores, total attempts, total sentences.
    const chapterGroups = useMemo(() => {
        if (!Array.isArray(readingChapterScores) || readingChapterScores.length === 0) return [];

        const round1 = (n) => Math.round(n * 10) / 10;
        const mean = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;

        // Map each assessment to the section (display-"Chapter") it belongs to so its score can be
        // blended into that chapter's average — mirroring the course-level reading+assessment blend.
        // Resolve the owning section from the entry's own sectionId, falling back to the lesson
        // plan's chapterId -> sectionId map when the entry's sectionId is missing.
        const sectionByChapterId = new Map();
        (lessonPlan?.sections || []).forEach((sec) => {
            (sec?.chapters || []).forEach((ch) => {
                if (ch?.chapterId && sec?.sectionId) sectionByChapterId.set(ch.chapterId, sec.sectionId);
            });
        });
        const assessmentPctBySection = new Map();
        const allAssessmentPcts = [];
        if (Array.isArray(assessmentSummary)) {
            for (const a of assessmentSummary) {
                const pct = assessmentPct(a);
                if (pct == null) continue;
                allAssessmentPcts.push(pct);
                const section = a.sectionId || sectionByChapterId.get(a.chapterId);
                if (!section) continue;
                if (!assessmentPctBySection.has(section)) assessmentPctBySection.set(section, []);
                assessmentPctBySection.get(section).push(pct);
            }
        }

        const map = new Map();
        for (const session of readingChapterScores) {
            const key = session.sectionId || '__no_section__';
            if (!map.has(key)) {
                map.set(key, {
                    sectionId: session.sectionId || null,
                    sectionName: session.sectionName || 'Chapter',
                    sessions: [],
                });
            }
            map.get(key).sessions.push(session);
        }
        let anyAssessmentMatched = false;
        const groups = Array.from(map.values()).map((g) => {
            // Reading half: use the deduplicated per-sentence mean from the same
            // aggregated attempts shown in the session modal.
            let readingAvg = readingAverageForSessions(g.sessions);
            if (readingAvg == null) {
                const bests = g.sessions
                    .map((s) => Number(s.bestAverage))
                    .filter((n) => Number.isFinite(n) && n > 0);
                readingAvg = bests.length ? round1(mean(bests)) : null;
            }
            // Blend this chapter's assessment score(s) into its average, using the same "mean of the
            // available halves" formula as courseAvgScore so chapter and course averages agree.
            const aPcts = assessmentPctBySection.get(g.sectionId) || [];
            if (aPcts.length) anyAssessmentMatched = true;
            const assessmentAvg = aPcts.length ? mean(aPcts) : null;
            const chapterAvg =
                readingAvg != null && assessmentAvg != null ? round1((readingAvg + assessmentAvg) / 2)
                : readingAvg != null ? readingAvg
                : assessmentAvg != null ? round1(assessmentAvg)
                : null;
            const totalAttempts = g.sessions.reduce((n, s) => n + (s.attemptCount || 0), 0);
            const totalSentences = g.sessions.reduce((n, s) => n + (s.totalSentences || 0), 0);
            const hasInProgress = g.sessions.some(
                (s) => Array.isArray(s.attempts) && s.attempts.some((a) => a.inProgress)
            );
            // Reading-retry "needs support" rollup: a chapter needs support if any of its
            // sessions does (a session is flagged at 3+ struggled sentences). Score-independent.
            const struggledSessions = g.sessions.filter((s) => s.needsSupport).length;
            const needsSupport = struggledSessions > 0;
            const totalReads = g.sessions.reduce((n, s) => n + (s.totalReads || 0), 0);
            // Sort sessions by name so "Session 1" comes before "Session 2".
            g.sessions.sort((a, b) =>
                String(a.chapterName || '').localeCompare(String(b.chapterName || ''), undefined, { numeric: true })
            );
            return { ...g, chapterAvg, readingAvg, assessmentAvg, totalAttempts, totalSentences, hasInProgress, needsSupport, struggledSessions, totalReads };
        });

        // Single-group fallback: if no assessment resolved into any chapter (e.g. every entry's
        // sectionId is null and the lesson-plan map couldn't place them) but there IS assessment
        // data and exactly one chapter, blend all assessment scores into that lone chapter. Covers
        // single-chapter courses where the assessment entry's sectionId doesn't line up.
        if (!anyAssessmentMatched && allAssessmentPcts.length && groups.length === 1) {
            const g = groups[0];
            const assessmentAvg = mean(allAssessmentPcts);
            g.assessmentAvg = assessmentAvg;
            g.chapterAvg =
                g.readingAvg != null ? round1((g.readingAvg + assessmentAvg) / 2) : round1(assessmentAvg);
        }

        // Sort chapters by name for stable display.
        groups.sort((a, b) =>
            String(a.sectionName || '').localeCompare(String(b.sectionName || ''), undefined, { numeric: true })
        );
        return groups;
    }, [readingChapterScores, assessmentSummary, lessonPlan]);

    // Curriculum position lookup from the lesson plan: sectionId -> index, and
    // chapterId -> {section, chapter}. lessonPlan.sections is already in course order —
    // it's what the study outline renders as "Chapter 1", "Chapter 2", ...
    const curriculumPosition = useMemo(() => {
        const sectionIndex = new Map();
        const chapterIndex = new Map();
        (lessonPlan?.sections || []).forEach((sec, si) => {
            if (sec?.sectionId) sectionIndex.set(sec.sectionId, si);
            (sec?.chapters || []).forEach((ch, ci) => {
                if (ch?.chapterId) chapterIndex.set(ch.chapterId, { section: si, chapter: ci });
            });
        });
        return { sectionIndex, chapterIndex };
    }, [lessonPlan]);

    // Club the per-attempt assessment rows into one entry per assessment. The backend returns
    // one row per attempt, so 3 attempts surface as 3 rows; group them by assessmentId and
    // surface the best attempt on the card. Full per-attempt detail (with pass/fail) is loaded
    // on click via openAttemptHistory -> the shared attempt-history modal.
    const assessmentGroups = useMemo(() => {
        const rows = Array.isArray(assessmentSummary) ? assessmentSummary : [];
        const byId = new Map();
        for (const a of rows) {
            const key = a.assessmentId || a.id;
            if (!byId.has(key)) byId.set(key, []);
            byId.get(key).push(a);
        }
        const groups = Array.from(byId.entries()).map(([assessmentId, attempts]) => {
            const pctOf = (x) => (x.total > 0 ? (x.scored / x.total) * 100 : -1);
            const best = attempts.reduce((acc, x) => (pctOf(x) > pctOf(acc) ? x : acc), attempts[0]);
            const latest = attempts.reduce(
                (acc, x) => (new Date(x.attendedOn || 0) >= new Date(acc.attendedOn || 0) ? x : acc),
                attempts[0]
            );
            const bestPct = best.total > 0 ? (best.scored / best.total) * 100 : null;

            // Per-attempt rows for the attempt-history modal, built locally from the summary
            // rows (each summary entry is one attempt). Oldest -> newest so #1 reads first;
            // `best` is flagged on the first attempt matching the top percentage.
            const sorted = [...attempts].sort(
                (a, b) => new Date(a.attendedOn || 0) - new Date(b.attendedOn || 0));
            let bestMarked = false;
            const attemptDtos = sorted.map((x, i) => {
                const pct = x.total > 0 ? (x.scored / x.total) * 100 : null;
                const isBest = !bestMarked && pct != null && bestPct != null && pct === bestPct;
                if (isBest) bestMarked = true;
                return {
                    id: x.id || `attempt-${i + 1}`,
                    attemptNumber: i + 1,
                    marksSecured: x.scored ?? 0,
                    totalMarks: x.total ?? 0,
                    percentage: pct,
                    gradeAchieved: (x.gradeAchieved || '').trim(),
                    finishedAt: x.attendedOn || null,
                    durationSec: x.timeTaken ?? null,
                    best: isBest,
                };
            });

            // Where this assessment sits in the course. Every attempt of one assessment
            // carries the same chapter/section, so the first row that has them wins.
            const placed = attempts.find((x) => x.chapterId || x.sectionId) || {};

            return {
                assessmentId,
                assessmentTitle: (attempts.find((x) => x.assessmentTitle) || {}).assessmentTitle || '',
                chapterId: placed.chapterId || null,
                sectionId: placed.sectionId || null,
                attemptCount: attempts.length,
                bestScored: best.scored ?? 0,
                bestTotal: best.total ?? 0,
                bestPct,
                grade: (best.gradeAchieved || '').trim(),
                latestAttendedOn: latest.attendedOn || null,
                attempts: attemptDtos,
            };
        });

        // Order the cards by curriculum position, not by when the student happened to sit
        // them. The endpoint returns rows most-recently-attended first, which floated a
        // Chapter 2 assessment above Chapter 1. Anything the lesson plan can't place (or
        // every card, while the plan is still loading) keeps the backend's newest-first order.
        const UNPLACED = Number.MAX_SAFE_INTEGER;
        const positionOf = (g) => {
            const byChapter = g.chapterId && curriculumPosition.chapterIndex.get(g.chapterId);
            if (byChapter) return byChapter;
            const si = g.sectionId ? curriculumPosition.sectionIndex.get(g.sectionId) : undefined;
            return { section: si ?? UNPLACED, chapter: UNPLACED };
        };
        return groups.sort((a, b) => {
            const pa = positionOf(a);
            const pb = positionOf(b);
            if (pa.section !== pb.section) return pa.section - pb.section;
            if (pa.chapter !== pb.chapter) return pa.chapter - pb.chapter;
            return String(b.latestAttendedOn || '').localeCompare(String(a.latestAttendedOn || ''));
        });
    }, [assessmentSummary, curriculumPosition]);

    // Overview stats across the course.
    const courseReadingSummary = useMemo(() => {
        const sessions = Array.isArray(readingChapterScores) ? readingChapterScores : [];
        const chapters = chapterGroups;
        const sessionBests = sessions
            .map((s) => Number(s.bestAverage))
            .filter((n) => Number.isFinite(n) && n > 0);
        const avg = sessionBests.length
            ? Math.round((sessionBests.reduce((a, b) => a + b, 0) / sessionBests.length) * 10) / 10
            : null;
        const totalAttempts = sessions.reduce((n, s) => n + (s.attemptCount || 0), 0);
        // Course-level "needs support": flag the student for this course when at least half
        // of their reading sessions are flagged (a session is flagged at 3+ struggled
        // sentences). Read-side only — no score impact.
        const flaggedSessions = sessions.filter((s) => s.needsSupport).length;
        const needsSupport = sessions.length > 0 && flaggedSessions / sessions.length >= 0.5;
        return {
            chapterCount: chapters.length,
            sessionCount: sessions.length,
            avg,
            totalAttempts,
            flaggedSessions,
            needsSupport,
        };
    }, [readingChapterScores, chapterGroups]);

    // Course avg: mean of the available reading and assessment halves. Reading
    // comes from the same deduplicated attempts used by chapter rollups.
    const courseAvgScore = useMemo(() => {
        const readingAvg = readingAverageForSessions(readingChapterScores);

        let aSum = 0, aCount = 0;
        if (Array.isArray(assessmentSummary)) {
            for (const a of assessmentSummary) {
                const pct = assessmentPct(a);
                if (pct == null) continue;
                aSum += pct; aCount += 1;
            }
        }
        const assessmentAvg = aCount ? aSum / aCount : null;

        if (readingAvg == null && assessmentAvg == null) return null;
        const combined = (readingAvg != null && assessmentAvg != null)
            ? (readingAvg + assessmentAvg) / 2
            : (readingAvg != null ? readingAvg : assessmentAvg);
        return Math.round(combined * 10) / 10;
    }, [readingChapterScores, assessmentSummary]);

    return (
        <div className={classes.dashboardContainer}>
            {/* Student list */}
            <div className={classes.studentSidebar}>
                <div className={classes.searchInput}>
                    <TextField
                        fullWidth
                        placeholder="Search courses"
                        variant="outlined"
                        value={searchTerm}
                        onChange={handleSearch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm.length > 0 && (
                                <InputAdornment position="end">
                                    <IconButton onClick={clearSearch}>
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </div>
                <div className={classes.courseList}>
                    {isLoading ? (
                        <Loader sx={{ backgroundColor: 'transparent' }} />
                    ) : filteredData.length === 0 ? (
                        <div
                            style={{
                                padding: '40px 16px',
                                textAlign: 'center',
                                color: '#6B7280',
                                fontSize: '0.9rem',
                                border: '1px dashed #E5E7EB',
                                borderRadius: 8,
                                background: '#F8FAFC',
                            }}
                        >
                            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#0F172A', marginBottom: 4 }}>
                                No courses found
                            </div>
                            <div style={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
                                {searchTerm
                                    ? 'No courses match your search.'
                                    : 'This student has no graded course activity yet.'}
                            </div>
                        </div>
                    ) : (
                        courseGroups.map((group) => {
                            const isCollapsed = collapsedLevels.has(group.key);
                            return (
                                <div key={group.key} className={classes.levelGroup}>
                                    <div
                                        className={classes.levelHeader}
                                        onClick={() => toggleLevel(group.key)}
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={!isCollapsed}
                                    >
                                        <span className={classes.levelHeaderTitle}>
                                            <ExpandMoreIcon
                                                className={`${classes.levelChevron} ${isCollapsed ? 'collapsed' : ''}`}
                                            />
                                            {group.levelName}
                                        </span>
                                        <span className={classes.levelCount}>
                                            {group.courses.length} course{group.courses.length === 1 ? '' : 's'}
                                        </span>
                                    </div>
                                    <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                                        <div className={classes.levelCourses}>
                                            {group.courses.map((course) => (
                                                <div
                                                    key={course.id}
                                                    className={`${classes.studentItem} ${selectedCourse?.courseName === course.courseName ? 'active' : ''
                                                        }`}
                                                    onClick={() => handleSelectionClick(course)}
                                                >
                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                        <strong style={{ fontSize: '0.95rem', color: '#0F172A' }}>{course.courseName}</strong>
                                                        <div className={classes.gradeNameText}>Code · {course.code}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Collapse>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Report Area */}
            <div className={classes.reportArea}>
                {(selectedCourse && Object.keys(selectedCourse).length) && userData ? (
                    <>
                        <div className={classes.reportHeader}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                <Avatar className={classes.avatar}>
                                    {avatar}
                                </Avatar>
                                <div>
                                    <Typography className={classes.studentName} variant="h5">{studentName}</Typography>
                                    <Typography variant="body2">{selectedCourse.courseName}</Typography>
                                </div>
                            </div>
                        </div>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 2,
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6" className={classes.specificsSection} sx={{ mb: 0 }}>
                                Reading Sessions
                            </Typography>
                            {(courseReadingSummary.sessionCount > 0) && (
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        size="small"
                                        label={`${courseReadingSummary.chapterCount} chapter${courseReadingSummary.chapterCount === 1 ? '' : 's'}`}
                                        sx={{ background: alpha('#002147', 0.08), color: '#002147', fontWeight: 600 }}
                                    />
                                    <Chip
                                        size="small"
                                        label={`${courseReadingSummary.sessionCount} session${courseReadingSummary.sessionCount === 1 ? '' : 's'}`}
                                        sx={{ background: alpha('#0EA5E9', 0.1), color: '#0EA5E9', fontWeight: 600 }}
                                    />
                                    <Chip
                                        size="small"
                                        label={`${courseReadingSummary.totalAttempts} attempt${courseReadingSummary.totalAttempts === 1 ? '' : 's'}`}
                                        sx={{ background: alpha('#6B7280', 0.1), color: '#6B7280', fontWeight: 600 }}
                                    />
                                    {courseAvgScore != null && (() => {
                                        const cc = colorForPct(courseAvgScore);
                                        return (
                                            <Chip
                                                size="small"
                                                label={`Course avg ${formatScore(courseAvgScore)}`}
                                                sx={{ background: cc.bg, color: cc.fg, fontWeight: 700 }}
                                            />
                                        );
                                    })()}
                                    {courseReadingSummary.needsSupport && (
                                        <Chip
                                            size="small"
                                            label={`Needs support · ${courseReadingSummary.flaggedSessions}/${courseReadingSummary.sessionCount} sessions`}
                                            sx={{ background: alpha('#D97706', 0.14), color: '#B45309', fontWeight: 700 }}
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>

                        {chapterScoresLoading ? (
                            <Loader sx={{ backgroundColor: 'transparent' }} />
                        ) : (chapterGroups && chapterGroups.length > 0) ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 4 }}>
                                {chapterGroups.map((group) => {
                                    const gc = colorForPct(group.chapterAvg);
                                    return (
                                        <Box
                                            key={group.sectionId || group.sectionName}
                                            sx={{
                                                background: '#fff',
                                                borderRadius: 2,
                                                border: '1px solid #E5E7EB',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {/* Chapter header bar */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    flexWrap: 'wrap',
                                                    gap: 1.5,
                                                    px: 2.5,
                                                    py: 1.5,
                                                    background: 'linear-gradient(90deg, #F8FAFC 0%, #FFFFFF 100%)',
                                                    borderBottom: '1px solid #E5E7EB',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                                    <Box
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: 1,
                                                            background: alpha('#002147', 0.08),
                                                            color: '#002147',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontWeight: 700,
                                                            fontSize: '0.85rem',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        📖
                                                    </Box>
                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Typography
                                                            sx={{ fontWeight: 700, fontSize: '1rem', color: '#0F172A' }}
                                                            noWrap
                                                        >
                                                            {group.sectionName || 'Chapter'}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                            {group.sessions.length} session{group.sessions.length === 1 ? '' : 's'} ·{' '}
                                                            {group.totalAttempts} attempt{group.totalAttempts === 1 ? '' : 's'} ·{' '}
                                                            {group.totalSentences} sentence{group.totalSentences === 1 ? '' : 's'} read
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    {/* "In progress" chip removed — the aggregator flags any
                                                        un-closed buffer as in-progress, which gives noisy
                                                        positives when activity events arrive out of order. */}
                                                    {group.chapterAvg != null && (
                                                        <Chip
                                                            label={`Chapter avg ${formatScore(group.chapterAvg)}`}
                                                            sx={{ background: gc.bg, color: gc.fg, fontWeight: 700 }}
                                                            size="small"
                                                        />
                                                    )}
                                                    {group.needsSupport && (
                                                        <Chip
                                                            label={`Needs support · ${group.struggledSessions}/${group.sessions.length} sessions`}
                                                            sx={{ background: alpha('#D97706', 0.14), color: '#B45309', fontWeight: 700 }}
                                                            size="small"
                                                        />
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Sessions grid */}
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    display: 'grid',
                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                                                    gap: 1.5,
                                                }}
                                            >
                                                {group.sessions.map((s) => {
                                                    const cc = colorForPct(s.bestAverage);
                                                    const sessionInProgress =
                                                        Array.isArray(s.attempts) && s.attempts.some((a) => a.inProgress);
                                                    return (
                                                        <Box
                                                            key={s.chapterId || Math.random()}
                                                            onClick={() => s.chapterId && openChapterAttemptHistory(s.chapterId)}
                                                            role="button"
                                                            tabIndex={0}
                                                            sx={{
                                                                p: 1.75,
                                                                borderRadius: 1.5,
                                                                background: '#fff',
                                                                cursor: s.chapterId ? 'pointer' : 'default',
                                                                border: '1px solid #E5E7EB',
                                                                borderLeft: `4px solid ${cc.fg}`,
                                                                transition: 'transform .15s, box-shadow .15s, border-color .15s',
                                                                '&:hover': {
                                                                    transform: 'translateY(-2px)',
                                                                    boxShadow: '0 6px 14px rgba(0,0,0,0.06)',
                                                                    borderColor: cc.fg,
                                                                },
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    gap: 1,
                                                                    mb: 1,
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{ fontWeight: 600, fontSize: '0.95rem' }}
                                                                    noWrap
                                                                >
                                                                    {s.chapterName || `Session ${String(s.chapterId || '').slice(0, 6)}`}
                                                                </Typography>
                                                                <Chip
                                                                    size="small"
                                                                    label={formatScore(s.bestAverage)}
                                                                    sx={{
                                                                        background: cc.bg,
                                                                        color: cc.fg,
                                                                        fontWeight: 700,
                                                                        height: 22,
                                                                    }}
                                                                />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                                                                <Chip
                                                                    size="small"
                                                                    label={`${s.totalSentences || 0} sentences`}
                                                                    sx={{
                                                                        background: alpha('#6B7280', 0.1),
                                                                        color: '#374151',
                                                                        height: 20,
                                                                        fontSize: '0.7rem',
                                                                    }}
                                                                />
                                                                {s.needsSupport && (
                                                                    <Chip
                                                                        size="small"
                                                                        label="Needs support"
                                                                        sx={{
                                                                            background: alpha('#D97706', 0.14),
                                                                            color: '#B45309',
                                                                            height: 20,
                                                                            fontSize: '0.7rem',
                                                                            fontWeight: 700,
                                                                        }}
                                                                    />
                                                                )}
                                                                {/* Per-session "In progress" chip removed — see comment on the
                                                                    chapter-header chip above. */}
                                                            </Box>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ color: '#6B7280', display: 'block' }}
                                                            >
                                                                Latest {formatScore(s.latestAverage)}
                                                                {s.lastAttemptAt
                                                                    ? ` · ${new Date(s.lastAttemptAt).toLocaleDateString()}`
                                                                    : ''}
                                                            </Typography>
                                                            {s.needsSupport && (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ color: '#B45309', display: 'block', mt: 0.25 }}
                                                                >
                                                                    {s.totalReads} reads · {s.struggledSentences} of{' '}
                                                                    {s.sentencesAttempted} sentences struggled
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    background: '#fff',
                                    border: '1px dashed #E5E7EB',
                                    borderRadius: 2,
                                    mb: 4,
                                }}
                            >
                                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                                    No reading sessions yet for this course.
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#9CA3AF', mt: 0.5, display: 'block' }}>
                                    Once the student completes a chapter, their per-session scores will appear here.
                                </Typography>
                            </Box>
                        )}

                        <Typography variant="h6" className={classes.specificsSection} gutterBottom>
                            Assessment Summary
                        </Typography>
                        {assessmentSummaryLoading ? (
                            <Loader sx={{ backgroundColor: 'transparent' }} />
                        ) : (assessmentGroups && assessmentGroups.length > 0) ? (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                                    gap: 2,
                                    mb: 4,
                                }}
                            >
                                {assessmentGroups.map((group) => {
                                    const c = colorForPct(group.bestPct);
                                    return (
                                        <Box
                                            key={group.assessmentId || Math.random()}
                                            onClick={() => group.assessmentId && openAttemptHistory(group)}
                                            role="button"
                                            tabIndex={0}
                                            sx={{
                                                p: 2.5,
                                                borderRadius: 2,
                                                background: '#fff',
                                                boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
                                                borderLeft: `5px solid ${c.fg}`,
                                                cursor: group.assessmentId ? 'pointer' : 'default',
                                                transition: 'transform .15s, box-shadow .15s, border-color .15s',
                                                '&:hover': {
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 8px 18px rgba(0,0,0,0.08)',
                                                },
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 600, mb: 0.75 }} noWrap>
                                                {group.assessmentTitle || `Test ${String(group.assessmentId || '').slice(0, 8)}`}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                <Chip
                                                    label={`${group.bestScored}/${group.bestTotal}`}
                                                    size="small"
                                                    sx={{ background: c.bg, color: c.fg, fontWeight: 700 }}
                                                />
                                                {group.grade && (
                                                    <Chip
                                                        label={`Grade ${group.grade}`}
                                                        size="small"
                                                        sx={{
                                                            background: alpha('#002147', 0.08),
                                                            color: '#002147',
                                                            fontWeight: 700,
                                                        }}
                                                    />
                                                )}
                                                <Chip
                                                    label={`${group.attemptCount} attempt${group.attemptCount === 1 ? '' : 's'}`}
                                                    size="small"
                                                    sx={{ background: alpha('#6B7280', 0.1), color: '#6B7280', fontWeight: 700 }}
                                                />
                                            </Box>
                                            <Typography variant="caption" sx={{ color: '#6B7280', display: 'block' }}>
                                                {group.latestAttendedOn ? new Date(group.latestAttendedOn).toLocaleString() : '—'}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{ color: '#6B7280', mb: 4 }}>
                                No assessments completed yet for this course.
                            </Typography>
                        )}

                        {/* Strengths & Needs Attention — temporarily hidden. The skill-level
                            rollups (Reading Fluency, Comprehension, etc.) are correct but the
                            data quality for the speech sub-scores (pronunciation/speed/smoothness/rhythm)
                            isn't there yet, so only 2 rows show in most cases. Re-enable by
                            uncommenting once the speech engine starts returning real outcome.* values.
                        <StrengthsWeaknessesSection
                            data={strengthsWeaknesses}
                            loading={strengthsWeaknessesLoading}
                        />
                        */}
                    </>
                ) : (
                    <Typography>Select a course to view details</Typography>
                )}
            </div>

            {/* Attempt-history modal (used for both tests and reading sessions) */}
            <Dialog
                open={attemptModalOpen}
                onClose={closeAttemptHistory}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pb: 0.5 }}>
                    {activeAttemptHistory?.kind === 'chapter' ? (
                        <Box>
                            {activeAttemptHistory?.sectionName && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: '#6B7280', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}
                                >
                                    {activeAttemptHistory.sectionName}
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.3 }}>
                                {activeAttemptHistory?.chapterName || 'Reading session attempts'}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: '1.15rem', fontWeight: 700 }}>
                            {activeAttemptHistory?.assessmentTitle || 'Attempt history'}
                        </Typography>
                    )}
                    {activeAttemptHistory && (() => {
                        const isChapter = activeAttemptHistory.kind === 'chapter';
                        const attempts = activeAttemptHistory.attempts || [];
                        const bestAttempt = attempts.find((a) => a.best) || null;
                        const latestAttempt = attempts.length
                            ? attempts.reduce((acc, a) =>
                                (acc?.attemptNumber || 0) >= (a.attemptNumber || 0) ? acc : a, attempts[0])
                            : null;
                        const fmtAssessmentScore = (a) =>
                            a == null
                                ? '—'
                                : `${a.marksSecured ?? 0}/${a.totalMarks ?? 0}`;
                        const bestLabel = isChapter
                            ? formatScore(activeAttemptHistory.bestAverage)
                            : fmtAssessmentScore(bestAttempt);
                        const latestLabel = isChapter
                            ? formatScore(activeAttemptHistory.latestAverage)
                            : fmtAssessmentScore(latestAttempt);
                        return (
                            <Typography variant="caption" component="div" sx={{ color: '#6B7280', mt: 0.5 }}>
                                {activeAttemptHistory.attemptCount} attempt{activeAttemptHistory.attemptCount === 1 ? '' : 's'} ·
                                best {bestLabel} ·
                                latest {latestLabel}
                            </Typography>
                        );
                    })()}
                </DialogTitle>
                <DialogContent dividers>
                    {activeAttemptHistoryLoading ? (
                        <Loader sx={{ backgroundColor: 'transparent' }} />
                    ) : !activeAttemptHistory || !activeAttemptHistory.attempts?.length ? (
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                            No attempts recorded yet.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {[...activeAttemptHistory.attempts]
                                .sort((a, b) => {
                                    // Passed attempts first (where a pass flag exists), then most
                                    // recent attempt first (descending attempt number).
                                    const pa = a.passed ? 1 : 0;
                                    const pb = b.passed ? 1 : 0;
                                    if (pa !== pb) return pb - pa;
                                    return (b.attemptNumber ?? 0) - (a.attemptNumber ?? 0);
                                })
                                .map((a) => {
                                const isChapter = activeAttemptHistory.kind === 'chapter';
                                const pct = isChapter ? a.averageReadingScore : a.percentage;
                                const c = colorForPct(pct);
                                const expandable =
                                    isChapter && Array.isArray(a.sentences) && a.sentences.length > 0;
                                const isExpanded = expandedAttempts.has(a.attemptNumber);
                                return (
                                    <Box
                                        key={a.id || `attempt-${a.attemptNumber}`}
                                        sx={{
                                            borderRadius: 2,
                                            border: `1px solid ${a.best ? c.fg : '#E5E7EB'}`,
                                            background: a.best ? alpha(c.fg, 0.06) : '#fff',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            onClick={expandable ? () => toggleAttempt(a.attemptNumber) : undefined}
                                            sx={{
                                                p: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 1.5,
                                                flexWrap: 'wrap',
                                                cursor: expandable ? 'pointer' : 'default',
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                <Typography sx={{ fontWeight: 700, minWidth: 26 }}>
                                                    #{a.attemptNumber}
                                                </Typography>
                                                {isChapter ? (
                                                    <>
                                                        <Chip
                                                            label={`${a.sentenceCount || 0} sentences`}
                                                            size="small"
                                                            sx={{ background: '#F3F4F6', color: '#111827', fontWeight: 600 }}
                                                        />
                                                        <Chip
                                                            label={formatScore(pct)}
                                                            size="small"
                                                            sx={{ background: c.bg, color: c.fg, fontWeight: 700 }}
                                                        />
                                                    </>
                                                ) : (
                                                    <Chip
                                                        label={`${a.marksSecured ?? 0}/${a.totalMarks ?? 0}`}
                                                        size="small"
                                                        sx={{ background: c.bg, color: c.fg, fontWeight: 700 }}
                                                    />
                                                )}
                                                {!isChapter && a.passed && (
                                                    <Chip
                                                        label="Passed"
                                                        size="small"
                                                        sx={{ background: alpha('#16A34A', 0.12), color: '#16A34A' }}
                                                    />
                                                )}
                                                {!isChapter && a.gradeAchieved && (
                                                    <Chip
                                                        label={`Grade ${a.gradeAchieved}`}
                                                        size="small"
                                                        sx={{ background: alpha('#002147', 0.08), color: '#002147', fontWeight: 700 }}
                                                    />
                                                )}
                                                {/* "In progress" chip removed in the attempts modal too. */}
                                                {a.best && (
                                                    <Chip
                                                        label="Best"
                                                        size="small"
                                                        sx={{ background: c.fg, color: '#fff', fontWeight: 700 }}
                                                    />
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                    {a.finishedAt ? new Date(a.finishedAt).toLocaleString() : ''}
                                                    {a.durationSec ? ` · ${a.durationSec}s` : ''}
                                                </Typography>
                                                {expandable && (
                                                    <ExpandMoreIcon
                                                        sx={{
                                                            color: '#6B7280',
                                                            transition: 'transform .2s',
                                                            transform: isExpanded ? 'rotate(180deg)' : 'none',
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                        {expandable && (
                                            <Collapse in={isExpanded} unmountOnExit>
                                                <Box sx={{ px: 1.5, pb: 1.5, pt: 0.25 }}>
                                                    {a.sentences.map((s, i) => {
                                                        const sc = colorForPct(s.score);
                                                        return (
                                                            <Box
                                                                key={i}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    gap: 1,
                                                                    py: 0.6,
                                                                    borderTop: '1px solid #F1F5F9',
                                                                }}
                                                            >
                                                                <Typography variant="body2" sx={{ color: '#374151' }}>
                                                                    {i + 1}. {s.text || '—'}
                                                                </Typography>
                                                                <Chip
                                                                    size="small"
                                                                    label={formatScore(s.score)}
                                                                    sx={{
                                                                        background: sc.bg,
                                                                        color: sc.fg,
                                                                        fontWeight: 700,
                                                                        flexShrink: 0,
                                                                    }}
                                                                />
                                                            </Box>
                                                        );
                                                    })}
                                                </Box>
                                            </Collapse>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAttemptHistory}>Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
